/**
 * WebAppChip — the setup chip for things web cannot do (PPL-407).
 *
 * Contacts and calendar sync live in the app, so on web the chip says so and
 * hands the visitor the /download ROUTE (the canvas wrote download.html; the
 * SPA route is the real page). The body is the kind's fixed label; the
 * sub-line is the payload's `reasonText` — the reason IS the copy (M15).
 */

import { Link } from 'react-router-dom';
import { Smartphone } from 'react-feather';
import type { ReactNode } from 'react';
import type { SetupPayload } from '../../types/home';
import { HomeDismiss, setupGlyph } from './HomeKit';

/** The five setup kinds' action labels — the only fixed copy a setup chip carries. */
export const SETUP_LABEL: Record<SetupPayload['kind'], string> = {
  contacts: 'Sync your contacts',
  calendar: 'Connect your calendar',
  notifications: 'Allow notifications',
  photo: 'Add a photo',
  handle: 'Choose a handle',
};

export function appOnlyBody(kind: SetupPayload['kind']): string {
  return `${SETUP_LABEL[kind]} in the app`;
}

export interface WebAppChipProps {
  kind: SetupPayload['kind'];
  /** `payload.reasonText` — "so I can put names to who you mention". */
  sub?: ReactNode;
  dismissable?: boolean;
  onDismiss?: () => void;
}

export function WebAppChip({ kind, sub, dismissable = true, onDismiss }: WebAppChipProps) {
  const body = appOnlyBody(kind);
  return (
    <div className="home-setup home-setup--web">
      <Link to="/download" className="home-setup-link">
        <span className="home-setup-glyph" aria-hidden="true">
          {setupGlyph(kind)}
        </span>
        <span className="home-setup-main">
          <span className="home-setup-body">{body}</span>
          {sub ? <span className="home-setup-sub">{sub}</span> : null}
        </span>
      </Link>
      <Link to="/download" className="home-setup-pill">
        <Smartphone size={12} aria-hidden="true" />
        get the app
      </Link>
      {dismissable && onDismiss ? <HomeDismiss onClick={onDismiss} /> : null}
    </div>
  );
}
